/**
 * The call detail drawer (docs/plans/oncall-line-plan.md 5, 6.2, 7.5, 7.7):
 * the attempt timeline, the two-voice transcript, the voicemail player, the
 * review, the callbacks, and "Call back" (<CallbackButton call={...} />,
 * WP-14's component; it decides for itself whether to show).
 *
 * It is driven by the route: CallsTab passes the :callId of
 * /oncall/calls/:callId, the link every alert email carries, so a deep link
 * and a click on a row open it the same way, and closing it goes back to
 * /oncall.
 *
 * GET /calls/:id writes an audit row (transcript read) every time, so the
 * detail is read once when the drawer opens (and again only after the
 * viewer places a callback or presses Retry), never by the list's poll. A
 * slow answer for a call or a site that is no longer shown is dropped.
 *
 * A call of another site answers 404 (the server never says it exists), so
 * an alert link opened on the wrong site says to switch site. Decryption
 * fails closed on the server; the drawer then shows nothing of the call.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CornerDownRight, Loader2, RefreshCw, X } from 'lucide-react';
import type { OncallCallDetail, OncallCallSummary } from '../../api/oncallApi';
import { apiErrorCode, apiErrorMessage, apiErrorStatus, getCall } from '../../api/oncallApi';
import { formatDateTime, formatDuration, maskPhone } from '../../lib/format';
import { secondaryBtnCls } from '../../../ui/styles';
import CallbackButton from '../CallbackButton';
import AttemptTimeline from './AttemptTimeline';
import { CallStatusPill, ReviewPill } from './CallPills';
import { answeredText } from './CallList';
import ReviewForm from './ReviewForm';
import TranscriptView from './TranscriptView';
import VoicemailPlayer from './VoicemailPlayer';
import { directionMeta } from './callMeta';

interface CallDetailDrawerProps {
  /** The call to show; null closes the drawer. */
  callId: string | null;
  /** The site whose line the call must belong to (a switch re-reads). */
  siteId: string | null;
  siteName: string | null | undefined;
  timeZone: string | null | undefined;
  onClose: () => void;
  /** A review changed the call: the list updates its row. */
  onCallChanged?: (call: OncallCallSummary) => void;
  /** A callback was placed: the list refreshes (the callback is a new row). */
  onCallbackPlaced?: () => void;
}

type DetailState =
  | { kind: 'loading' }
  | { kind: 'ready'; call: OncallCallDetail }
  | { kind: 'error'; message: string; code: string | null };

/** The words for a detail that could not be shown. */
export function detailErrorMessage(err: unknown, siteName: string | null | undefined): string {
  const code = apiErrorCode(err);
  const status = apiErrorStatus(err);
  if (status === 404 || code === 'ONCALL_NOT_FOUND') {
    const site = siteName ? `${siteName}’s` : 'this site’s';
    return `This call is not on ${site} 24-hour line. If you opened it from an alert, switch to that call’s site in the header.`;
  }
  if (code === 'ONCALL_ENCRYPTION_KEY_UNAVAILABLE') {
    return 'This call cannot be opened right now: the line’s encryption key is not configured. Nothing of the call is shown.';
  }
  if (code === 'ONCALL_DECRYPT_FAILED') {
    return 'This call’s record could not be decrypted, so nothing of it is shown. Tell a line admin.';
  }
  if (code === 'ONCALL_AUDIT_FAILED') {
    return 'This call could not be opened because the read could not be recorded in the audit log. Try again.';
  }
  if (status === null) return 'The call could not be loaded. Check your connection and try again.';
  return apiErrorMessage(err, 'The call could not be loaded.');
}

function Section({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section className="border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-5" data-testid={testId}>
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">{title}</h3>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="truncate text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function CallLinkRow({ call, timeZone }: { call: OncallCallSummary; timeZone: string | null | undefined }) {
  return (
    <Link
      to={`/oncall/calls/${encodeURIComponent(call.id)}`}
      className="flex min-h-[44px] flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700/50 sm:min-h-0"
      data-testid={`oncall-related-${call.id}`}
    >
      <CornerDownRight className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
      <span className="tabular-nums">{formatDateTime(call.started_at, timeZone)}</span>
      <CallStatusPill status={call.status} />
      <span className="text-gray-500 dark:text-gray-400">{answeredText(call)}</span>
    </Link>
  );
}

export default function CallDetailDrawer({
  callId,
  siteId,
  siteName,
  timeZone,
  onClose,
  onCallChanged,
  onCallbackPlaced,
}: CallDetailDrawerProps) {
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = 'oncall-call-drawer-title';

  // Read the call: once per (call, site), and again only on an explicit reload.
  useEffect(() => {
    if (!callId) return undefined;
    const request = ++requestRef.current;
    setState({ kind: 'loading' });
    getCall(callId)
      .then((call) => {
        if (request === requestRef.current) setState({ kind: 'ready', call });
      })
      .catch((err) => {
        if (request === requestRef.current) {
          setState({ kind: 'error', message: detailErrorMessage(err, siteName), code: apiErrorCode(err) });
        }
      });
    return () => {
      // A newer call, a site switch or a close drops this answer.
      requestRef.current += 1;
    };
    // siteName only words the error; it must not trigger another audited read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, siteId, reloadKey]);

  // Escape closes; the close button takes focus when a call opens.
  useEffect(() => {
    if (!callId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A dialog opened above this one (the callback confirm) closes first.
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs.length > 0 && dialogs[dialogs.length - 1] !== panelRef.current) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [callId, onClose]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  const handleReviewed = useCallback(
    (updated: OncallCallSummary & { review_note?: string | null }) => {
      setState((prev) => (prev.kind === 'ready' && prev.call.id === updated.id ? { kind: 'ready', call: { ...prev.call, ...updated } } : prev));
      onCallChanged?.(updated);
    },
    [onCallChanged],
  );

  const handleCallbackPlaced = useCallback(() => {
    onCallbackPlaced?.();
    reload();
  }, [onCallbackPlaced, reload]);

  if (!callId) return null;

  const call = state.kind === 'ready' ? state.call : null;
  const dir = directionMeta(call?.direction);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="oncall-call-drawer-overlay"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-full w-full flex-col bg-white shadow-xl dark:bg-gray-800 sm:max-w-xl sm:border-l sm:border-gray-200 sm:dark:border-gray-700"
        data-testid="oncall-call-drawer"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="oncall-call-drawer-title">
              {call ? dir.title : 'Call'}
            </h2>
            {call && (
              <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="oncall-call-when">
                {formatDateTime(call.started_at, timeZone, { withZone: true })}
              </p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close call details"
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
            data-testid="oncall-call-drawer-close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {state.kind === 'loading' && (
            <div className="flex justify-center py-12">
              <Loader2 role="status" aria-label="Loading the call" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
          )}

          {state.kind === 'error' && (
            <div className="space-y-3 px-4 py-6 sm:px-5" data-testid="oncall-call-error">
              <p role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{state.message}</span>
              </p>
              {state.code !== 'ONCALL_NOT_FOUND' && (
                <button type="button" onClick={reload} className={secondaryBtnCls} data-testid="oncall-call-retry">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Try again
                </button>
              )}
            </div>
          )}

          {call && (
            <>
              <div className="space-y-4 px-4 py-4 sm:px-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <CallStatusPill status={call.status} />
                  <ReviewPill review={call.review_status} />
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <Fact label="Length">{formatDuration(call.duration_sec)}</Fact>
                  <Fact label={call.direction === 'callback' ? 'Called by' : 'Answered by'}>
                    <span data-testid="oncall-call-answered-by">
                      {call.direction === 'callback' ? call.answered_by_name || 'A team member' : answeredText(call)}
                    </span>
                  </Fact>
                  <Fact label={call.direction === 'callback' ? 'Number called' : 'Caller'}>
                    <span className="tabular-nums" data-testid="oncall-call-caller">
                      {call.caller_last4 ? maskPhone(call.caller_last4) : 'Not recorded'}
                    </span>
                  </Fact>
                  {call.answered_at && <Fact label="Answered at">{formatDateTime(call.answered_at, timeZone)}</Fact>}
                </dl>
                <div className="empty:hidden" data-testid="oncall-callback-slot">
                  <CallbackButton call={call} onPlaced={handleCallbackPlaced} />
                </div>
              </div>

              {call.parent_call_id && (
                <Section title="Callback for" testId="oncall-call-parent">
                  <Link
                    to={`/oncall/calls/${encodeURIComponent(call.parent_call_id)}`}
                    className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0"
                  >
                    <CornerDownRight className="h-4 w-4" aria-hidden="true" />
                    Open the original call
                  </Link>
                </Section>
              )}

              {call.has_voicemail && (
                <Section title="Voicemail">
                  <VoicemailPlayer key={call.id} callId={call.id} durationSec={call.voicemail_duration_sec} />
                </Section>
              )}

              <Section title="Transcript">
                <TranscriptView segments={call.transcript ?? []} timeZone={timeZone} />
              </Section>

              {call.direction !== 'callback' && (
                <Section title="Who was rung">
                  <AttemptTimeline attempts={call.attempts ?? []} timeZone={timeZone} ringing={call.status === 'ringing'} />
                </Section>
              )}

              {(call.callbacks?.length ?? 0) > 0 && (
                <Section title="Callbacks" testId="oncall-call-callbacks">
                  <ul className="space-y-1">
                    {(call.callbacks ?? []).map((cb) => (
                      <li key={cb.id}>
                        <CallLinkRow call={cb} timeZone={timeZone} />
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section title="Review">
                <ReviewForm call={call} timeZone={timeZone} onSaved={handleReviewed} />
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
